'use client';

import { useState } from 'react';
import { useDesitnationCollection } from '@commons/DesitnationCollection/useDesitnationCollection';
import HtmlToJsx from '@commons/HtmlToJsx/HtmlToJsx';
import useDictionary from '@modules/Dictionary/useDictionary';
import BlurbList from '@commons/BlurbList/BlurbList';
import Blurb from '@commons/Blurb/Blurb';
import Checkbox from '@commons/Checkbox/Checkbox';
import './_map_sidebar.scss';

export default function MapSidebar({
  areas,
  initialTab = 'NAVIGATE',
  onAreaClick,
  selectedCategories = [],
  onSetSelectedCategories,
  onCategoryToggle,
  visibleProducts = [],
  hoveredProductId,
  onProductHover,
  onProductClick
}) {
  const { dictionary } = useDictionary();
  const [activeTab, setActiveTab] = useState(initialTab); // Default flik: BOOKING, NAVIGATE, EXPERIENCES
  const { categoriesTree } = useDesitnationCollection();

  // Innehåll för "NAVIGATE" fliken
  const renderNavigate = () => (
    <>
      <div className="MapSidebar__title-row">
        <h1 className="MapSidebar__title">{dictionary?.map?.titleNavigate || 'Navigate Telemark'}</h1>
      </div>
      <div className="MapSidebar__regions">
        {areas.map((area) => (
          <div key={area.name} className="MapSidebar__region">
            <h2
              className={area.coordinates ? 'is-clickable' : ''}
              onClick={() => {
                if (onAreaClick && area.coordinates) {
                  onAreaClick(area.coordinates, area.zoom || 8);
                }
              }}
            >
              {dictionary?.map?.areas?.[area.key] || area.name}
            </h2>
            {area.children && area.children.length > 0 && (
              <ul>
                {area.children.map((child) => (
                  <li
                    key={child.name}
                    onClick={() => {
                      if (onAreaClick && child.coordinates) {
                        onAreaClick(child.coordinates, child.zoom || 10);
                      }
                    }}
                  >
                    {child.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </>
  );

  // Innehåll för "EXPERIENCES" fliken
  const renderExperiences = () => {
    const allCategoryIds = categoriesTree?.flatMap((c) => c.children?.map((child) => child.id) || []) || [];
    const isAllSelected = allCategoryIds.length > 0 && selectedCategories.length === allCategoryIds.length;

    const handleSelectAllToggle = () => {
      if (isAllSelected) {
        onSetSelectedCategories?.([]);
      } else {
        onSetSelectedCategories?.(allCategoryIds);
      }
    };

    return (
      <>
        <div className="MapSidebar__title-row">
          <h1 className="MapSidebar__title">{dictionary?.map?.titleExperiences || 'Services'}</h1>
          <Checkbox className="MapSidebar__select-all" checked={isAllSelected} onChange={handleSelectAllToggle}>
            {isAllSelected ? dictionary?.map?.clearAll || 'Clear all' : dictionary?.map?.selectAll || 'Select all'}
          </Checkbox>
        </div>

        <div className="MapSidebar__regions">
          {categoriesTree?.map((category) => (
            <div key={category.id} className="MapSidebar__region MapSidebar__region--experiences">
              <h2>
                <HtmlToJsx html={category.title} />
              </h2>
              {category.children && category.children.length > 0 && (
                <ul>
                  {category.children.map((child) => (
                    <li key={child.id}>
                      <Checkbox
                        checked={selectedCategories.includes(child.id)}
                        onChange={() => {
                          if (onCategoryToggle) {
                            onCategoryToggle(child.id);
                          }
                        }}
                      >
                        <HtmlToJsx html={child.title} />
                      </Checkbox>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  // Innehåll för "BOOKING" fliken (Platshållare eftersom designen saknas i detta steg)
  const renderBooking = () => (
    <>
      <div className="MapSidebar__title-row">
        <h1 className="MapSidebar__title">{dictionary?.map?.titleBooking || 'Booking'}</h1>
      </div>
      <div className="MapSidebar__products">
        <BlurbList.Map>
          {visibleProducts?.map((p) => (
            <Blurb variant={'map'} key={p.id} onClick={() => onProductClick(p)}>
              <Blurb.Image src={p.image} alt={p.title} loading="lazy" />
              <Blurb.Content>
                <Blurb.Title level={3}>{p.title}</Blurb.Title>
                {p.address && <Blurb.Address>{p.address}</Blurb.Address>}
                <Blurb.Actions>
                  <Blurb.CTA
                    primaryText={dictionary?.product?.bookNow}
                    primaryHref={p.booking_url}
                    secondaryText={dictionary?.product?.readMore}
                    secondaryHref={p.url}
                  />
                </Blurb.Actions>
              </Blurb.Content>
            </Blurb>
          ))}
        </BlurbList.Map>
      </div>
    </>
  );

  return (
    <aside className="MapSidebar">
      <div className="MapSidebar__content">
        {activeTab === 'NAVIGATE' && renderNavigate()}
        {activeTab === 'EXPERIENCES' && renderExperiences()}
        {activeTab === 'BOOKING' && renderBooking()}
      </div>

      <div className="MapSidebar__bottom-nav">
        <button
          className={`MapSidebar__nav-item ${activeTab === 'BOOKING' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('BOOKING')}
        >
          <span className="MapSidebar__nav-item-text">
            {dictionary?.map?.tabBooking || 'BOOKING'}
            <span className="MapSidebar__product-count">{visibleProducts?.length || 0}</span>
          </span>
        </button>
        <button
          className={`MapSidebar__nav-item ${activeTab === 'EXPERIENCES' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('EXPERIENCES')}
        >
          <span className="MapSidebar__nav-item-text">
            {dictionary?.map?.tabExperiences || 'EXPERIENCES'}
            {selectedCategories?.length > 0 && (
              <span className="MapSidebar__product-count">{selectedCategories.length}</span>
            )}
          </span>
        </button>
        <button
          className={`MapSidebar__nav-item ${activeTab === 'NAVIGATE' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('NAVIGATE')}
        >
          <span>{dictionary?.map?.tabNavigate || 'NAVIGATE'}</span>
        </button>
      </div>
    </aside>
  );
}
